import React, { useState, useRef, useEffect } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { authApi, tecniciApi, clientiFinaliApi, beniApi, allegatiTecniciApi, verbaliApi, menuPermissionsApi, richiesteDocApi, rfiApi } from '../services/api'
import { getMezzi } from '../services/fleet'
import { useAuth } from '../contexts/AuthContext'
import { Tecnico } from '../types'
import { Plus, Pencil, Trash2, Shield, UserCheck, XCircle, Users, Wrench, AlertTriangle, MapPin, Car, Phone, Calendar, Upload, Download, FileText, Paperclip, FileUp, Landmark, Euro, Crop, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import PhotoCropModal from '../components/PhotoCropModal'

const ROLES = [
  { value: 'superadmin', label: 'Super Admin', cls: 'bg-purple-100 text-purple-700' },
  { value: 'admin', label: 'Admin', cls: 'bg-blue-100 text-blue-700' },
  { value: 'dispatcher', label: 'Dispatcher', cls: 'bg-cyan-100 text-cyan-700' },
  { value: 'responsabile_tecnico', label: 'Resp. Tecnico', cls: 'bg-teal-100 text-teal-700' },
  { value: 'tecnico_senior', label: 'Tecnico Senior', cls: 'bg-green-100 text-green-700' },
  { value: 'tecnico', label: 'Tecnico', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'contabilita', label: 'Contabilit\u00e0', cls: 'bg-amber-100 text-amber-700' },
  { value: 'committente', label: 'Committente', cls: 'bg-orange-100 text-orange-700' },
  { value: 'cliente_finale', label: 'Cliente Finale', cls: 'bg-slate-100 text-slate-600' },
  { value: 'readonly', label: 'Sola Lettura', cls: 'bg-red-100 text-red-700' },
]

const CAT_LABELS: Record<string, string> = {
  dipendente_diretto: 'Dip. Diretto', cocooco: 'Co.Co.Co',
  subappaltatore: 'Subappaltatore', impresa_terza: 'Impresa Terza',
}
const LIVELLO_LABELS: Record<string, string> = {
  base: 'Tecnico', senior: 'Senior', capo_squadra: 'Capo Squadra',
}

const roleCls = (role: string) => ROLES.find(r => r.value === role)?.cls || 'bg-slate-100 text-slate-600'
const roleLabel = (role: string) => ROLES.find(r => r.value === role)?.label || role

interface UserItem {
  id: number; email: string; role: string; nome?: string; cognome?: string; telefono_whatsapp?: string; is_active: boolean
}

const AVATAR_COLORS = ['bg-blue-600', 'bg-purple-600', 'bg-emerald-600', 'bg-orange-600', 'bg-rose-600']

function useListinoManodoperaRfi() {
  return useQuery({
    queryKey: ['rfi-listino-manodopera'],
    queryFn: () => rfiApi.listinoManodopera().then(r => r.data as { tariffa: string; descrizione: string; prezzo: number }[]),
    staleTime: 5 * 60_000,
  })
}

function TecnicoAvatar({ tecnico, size = 44 }: { tecnico: Tecnico; size?: number }) {
  const initials = `${tecnico.nome[0]}${tecnico.cognome[0]}`
  const ci = (tecnico.nome.charCodeAt(0) + tecnico.cognome.charCodeAt(0)) % AVATAR_COLORS.length
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false
    if (tecnico.foto_source) {
      tecniciApi.getFoto(tecnico.id).then(r => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(r.data)
        setUrl(objectUrl)
      }).catch(() => { if (!cancelled) setUrl(null) })
    } else {
      setUrl(null)
    }
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [tecnico.id, tecnico.foto_source])

  if (url) {
    return (
      <img
        src={url}
        alt={`${tecnico.nome} ${tecnico.cognome}`}
        className="rounded-xl object-cover flex-shrink-0"
        style={{ width: size, height: size }}
      />
    )
  }
  return (
    <div
      className={clsx('rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0', AVATAR_COLORS[ci])}
      style={{ width: size, height: size }}
    >
      {initials}
    </div>
  )
}

export default function Utenti() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const [tab, setTab] = useState<'utenti' | 'tecnici'>('utenti')
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [showTecnicoForm, setShowTecnicoForm] = useState(false)
  const [editing, setEditing] = useState<UserItem | null>(null)
  const [editingTecnico, setEditingTecnico] = useState<Tecnico | null>(null)
  const [viewingTecnico, setViewingTecnico] = useState<Tecnico | null>(null)
  const [tecnicoDaEliminare, setTecnicoDaEliminare] = useState<Tecnico | null>(null)
  const [confirmDeleteForce, setConfirmDeleteForce] = useState(false)
  const [showRichiestaDoc, setShowRichiestaDoc] = useState<Tecnico | null>(null)
  const importUsersRef = useRef<HTMLInputElement>(null)
  const importTecniciRef = useRef<HTMLInputElement>(null)
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const isReadonly = user?.role === 'readonly'

  const geocodeMut = useMutation({
    mutationFn: () => clientiFinaliApi.geocodeMissing(),
    onSuccess: (res: any) => {
      toast.success(`Geocodificati ${res.data.aggiornati} indirizzi su ${res.data.totale_processati}`)
    },
    onError: () => toast.error('Errore geocoding'),
  })

  const exportUsersMut = useMutation({
    mutationFn: () => authApi.exportUsersXlsx(),
    onSuccess: (res: any) => {
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a'); a.href = url; a.download = 'utenti.xlsx'; a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Errore export utenti'),
  })

  const importUsersMut = useMutation({
    mutationFn: (file: File) => authApi.importUsersXlsx(file),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['users'] })
      toast.success(`Importati ${res.data.importati}, saltati ${res.data.saltati}`)
    },
    onError: () => toast.error('Errore import utenti'),
  })

  const exportTecniciMut = useMutation({
    mutationFn: () => tecniciApi.exportXlsx(),
    onSuccess: (res: any) => {
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a'); a.href = url; a.download = 'tecnici.xlsx'; a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Errore export tecnici'),
  })

  const importTecniciMut = useMutation({
    mutationFn: (file: File) => tecniciApi.importXlsx(file),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['tecnici'] })
      toast.success(`Importati ${res.data.importati}, saltati ${res.data.saltati}`)
    },
    onError: () => toast.error('Errore import tecnici'),
  })

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => authApi.listUsers().then(r => r.data as UserItem[]),
  })

  const { data: tecniciData, isLoading: tecniciLoading } = useQuery({
    queryKey: ['tecnici'],
    queryFn: () => tecniciApi.list({ per_page: 100 }).then(r => r.data),
  })

  const { data: scadenze } = useQuery({
    queryKey: ['scadenze-allarme'],
    queryFn: () => tecniciApi.scadenzeAllarme(30).then(r => r.data),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => authApi.deleteUser(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); toast.success('Utente eliminato') },
    onError: () => toast.error('Errore eliminazione'),
  })

  const eliminaTecnicoMut = useMutation({
    mutationFn: ({ id, force }: { id: number; force: boolean }) => tecniciApi.delete(id, force),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['tecnici'] })
      qc.invalidateQueries({ queryKey: ['tecnici-select'] })
      const disc = res.data?.pratiche_disassegnate ?? 0
      toast.success(disc > 0 ? `Tecnico eliminato. ${disc} pratiche disassegnate.` : 'Tecnico eliminato con successo.')
      setTecnicoDaEliminare(null)
      setConfirmDeleteForce(false)
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail || ''
      if (detail.includes('pratiche aperte')) {
        setConfirmDeleteForce(true)
      } else {
        toast.error('Errore eliminazione: ' + detail)
      }
    },
  })

  const tecnici: Tecnico[] = tecniciData?.items ?? []

  const q = search.trim().toLowerCase()
  const usersFiltrati = !q ? users : users?.filter(u =>
    `${u.nome} ${u.cognome} ${u.email} ${u.role}`.toLowerCase().includes(q)
  )
  const tecniciFiltrati = !q ? tecnici : tecnici.filter(t =>
    `${t.nome} ${t.cognome} ${t.email_operativa || ''} ${t.codice_fiscale || ''} ${t.veicolo_targa || ''}`.toLowerCase().includes(q)
  )

  return (
    <div className="space-y-4">
      {ConfirmModal}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Gestione Utenti</h1>
          <p className="text-slate-500 text-sm">{users?.length ?? 0} utenti, {tecnici.length} tecnici</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button
              onClick={() => geocodeMut.mutate()}
              disabled={geocodeMut.isPending}
              className="btn-secondary text-sm"
              title="Geocodifica automatica indirizzi senza coordinate GPS"
            >
              🗺 {geocodeMut.isPending ? 'Geocoding in corso...' : 'Geocodifica indirizzi'}
            </button>
          )}
          {tab === 'utenti' && isAdmin && (
            <>
              <input ref={importUsersRef} type="file" accept=".xlsx" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) { importUsersMut.mutate(f); e.target.value = '' } }} />
              <button onClick={() => exportUsersMut.mutate()} disabled={exportUsersMut.isPending} className="btn-secondary text-sm">
                <Download size={14} /> Esporta
              </button>
              <button onClick={() => importUsersRef.current?.click()} disabled={importUsersMut.isPending} className="btn-secondary text-sm">
                <Upload size={14} /> Importa
              </button>
            </>
          )}
          {tab === 'tecnici' && isAdmin && (
            <>
              <input ref={importTecniciRef} type="file" accept=".xlsx" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) { importTecniciMut.mutate(f); e.target.value = '' } }} />
              <button onClick={() => exportTecniciMut.mutate()} disabled={exportTecniciMut.isPending} className="btn-secondary text-sm">
                <Download size={14} /> Esporta
              </button>
              <button onClick={() => importTecniciRef.current?.click()} disabled={importTecniciMut.isPending} className="btn-secondary text-sm">
                <Upload size={14} /> Importa
              </button>
            </>
          )}
          {tab === 'utenti' && !isReadonly && (
            <button onClick={() => { setEditing(null); setShowForm(true) }} className="btn-primary"><Plus size={16} />Nuovo Utente</button>
          )}
          {tab === 'tecnici' && !isReadonly && (
            <button onClick={() => setShowTecnicoForm(true)} className="btn-primary"><Plus size={16} />Nuovo Tecnico</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {([['utenti', 'Utenti', Users], ['tecnici', 'Tecnici', Wrench]] as [string, string, React.ElementType][])
          .map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key as any)}
            className={clsx('flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            )}>
            <Icon size={15} />{label}
            {key === 'tecnici' && scadenze?.length > 0 && (
              <span className="bg-amber-500 text-white text-xs rounded-full px-1.5 min-w-[18px] text-center">{scadenze.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Barra di ricerca */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          className="input pl-9 w-full"
          placeholder={tab === 'utenti' ? 'Cerca utente per nome, email, ruolo...' : 'Cerca tecnico per nome, email, CF, targa...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Tab Utenti */}
      {tab === 'utenti' && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50/50">
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Nome</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Email</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Ruolo</th>
                <th className="text-center px-4 py-3 font-semibold text-slate-600">Stato</th>
                <th className="text-center px-4 py-3 font-semibold text-slate-600">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan={5} className="py-10 text-center text-slate-400">Caricamento...</td></tr>
              ) : usersFiltrati?.map((u) => (
                <tr key={u.id} className="table-row-hover">
                  <td className="px-4 py-3 font-medium">{u.nome} {u.cognome}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3"><span className={clsx('badge', roleCls(u.role))}>{roleLabel(u.role)}</span></td>
                  <td className="px-4 py-3 text-center">
                    {u.is_active
                      ? <span className="badge bg-green-100 text-green-700"><UserCheck size={12} className="mr-1" />Attivo</span>
                      : <span className="badge bg-red-100 text-red-700"><XCircle size={12} className="mr-1" />Disattivo</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      {!isReadonly && (
                        <>
                          <button onClick={() => { setEditing(u); setShowForm(true) }} className="btn-ghost px-2 py-1 text-amber-600" title="Modifica"><Pencil size={14} /></button>
                          <button onClick={() => confirmDelete(`Eliminare ${u.email}?`, () => deleteMut.mutate(u.id))} className="btn-ghost px-2 py-1 text-red-600" title="Elimina"><Trash2 size={14} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab Tecnici */}
      {tab === 'tecnici' && (
        <>
          {scadenze?.length > 0 && (
            <div className="card border-amber-200 bg-amber-50 p-4">
              <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <AlertTriangle size={16} /> Scadenze documenti nei prossimi 30 giorni
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {scadenze.map((s: any, i: number) => (
                  <div key={i} className={clsx('rounded-lg p-3 text-sm border',
                    s.giorni_rimanenti <= 7 ? 'bg-red-50 border-red-200' : s.giorni_rimanenti <= 15 ? 'bg-orange-50 border-orange-200' : 'bg-yellow-50 border-yellow-200'
                  )}>
                    <div className="font-medium text-slate-800">{s.tecnico_nome}</div>
                    <div className="text-slate-600">{s.tipo}</div>
                    <div className={clsx('font-semibold mt-0.5', s.giorni_rimanenti <= 7 ? 'text-red-600' : 'text-amber-700')}>
                      {s.giorni_rimanenti <= 0 ? 'SCADUTO' : `${s.giorni_rimanenti} giorni`} \u2014 {s.scadenza}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {tecniciLoading ? (
              <div className="col-span-full text-center py-12 text-slate-400">Caricamento...</div>
            ) : tecniciFiltrati.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-400">{q ? 'Nessun tecnico trovato' : 'Nessun tecnico'}</div>
            ) : tecniciFiltrati.map((t) => {
              return (
                <div
                  key={t.id}
                  onClick={() => setViewingTecnico(t)}
                  className={clsx('card p-5 hover:shadow-md transition-shadow cursor-pointer', t.scadenze_in_allarme && 'border-amber-200')}
                >
                  {t.scadenze_in_allarme && <div className="flex items-center gap-1.5 text-amber-600 text-xs mb-3 bg-amber-50 px-2 py-1 rounded-md"><AlertTriangle size={12} /> Documenti in scadenza</div>}
                  <div className="flex items-center gap-3 mb-4">
                    <TecnicoAvatar tecnico={t} size={44} />
                    <div>
                      <div className="font-semibold text-slate-900">{t.nome} {t.cognome}</div>
                      <div className="text-xs text-slate-500">{LIVELLO_LABELS[t.livello_accesso] || t.livello_accesso}</div>
                    </div>
                    <div className={clsx('ml-auto w-2 h-2 rounded-full', t.is_active ? 'bg-green-500' : 'bg-slate-300')} />
                  </div>
                  <div className="space-y-1.5 text-xs text-slate-600">
                    {t.zona_primaria && <div className="flex items-center gap-1.5"><MapPin size={12} className="text-slate-400" />{t.zona_primaria}{t.zona_secondaria && ` \u2022 ${t.zona_secondaria}`}</div>}
                    {t.veicolo_targa && <div className="flex items-center gap-1.5"><Car size={12} className="text-slate-400" />{t.veicolo_targa}</div>}
                    {t.telefono && <div className="flex items-center gap-1.5"><Phone size={12} className="text-slate-400" />{t.telefono}</div>}
                    {t.banca && <div className="flex items-center gap-1.5"><Landmark size={12} className="text-slate-400" />{t.banca}{t.iban && ` • ${t.iban}`}</div>}
                    {!t.banca && t.iban && <div className="flex items-center gap-1.5"><Landmark size={12} className="text-slate-400" />{t.iban}</div>}
                    {!!t.costo_orario && <div className="flex items-center gap-1.5"><Euro size={12} className="text-slate-400" />{t.costo_orario.toFixed(2)}/h</div>}
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className="badge bg-slate-100 text-slate-600 text-xs">{CAT_LABELS[t.categoria_contrattuale] || t.categoria_contrattuale}</span>
                    <div className="flex items-center gap-1">
                      {isAdmin && !isReadonly && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowRichiestaDoc(t) }}
                          className="btn-ghost px-2 py-1 text-blue-500"
                          title="Richiedi documento"
                        >
                          <FileUp size={13} />
                        </button>
                      )}
                      {isAdmin && !isReadonly && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setTecnicoDaEliminare(t); setConfirmDeleteForce(false) }}
                          className="btn-ghost px-2 py-1 text-red-500"
                          title="Elimina tecnico"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {showForm && (
        <UserFormModal user={editing} onClose={() => { setShowForm(false); setEditing(null) }}
          onSuccess={() => { setShowForm(false); setEditing(null); qc.invalidateQueries({ queryKey: ['users'] }); toast.success(editing ? 'Utente aggiornato!' : 'Utente creato!') }} />
      )}
      {showTecnicoForm && (
        <CreateTecnicoModal onClose={() => setShowTecnicoForm(false)}
          onSuccess={() => {
            setShowTecnicoForm(false)
            qc.invalidateQueries({ queryKey: ['tecnici'] })
            qc.invalidateQueries({ queryKey: ['tecnici-select'] })
            qc.invalidateQueries({ queryKey: ['tecnici-bulk'] })
            qc.invalidateQueries({ queryKey: ['users'] })
            toast.success('Tecnico creato!')
          }} />
      )}
      {editingTecnico && (
        <EditTecnicoModal
          tecnico={editingTecnico}
          onClose={() => setEditingTecnico(null)}
          onSuccess={() => {
            setEditingTecnico(null)
            qc.invalidateQueries({ queryKey: ['tecnici'] })
            qc.invalidateQueries({ queryKey: ['scadenze-allarme'] })
            toast.success('Tecnico aggiornato!')
          }}
        />
      )}
      {viewingTecnico && (
        <TecnicoViewModal
          tecnico={viewingTecnico}
          canEdit={isAdmin && !isReadonly}
          onClose={() => setViewingTecnico(null)}
          onEdit={() => { setEditingTecnico(viewingTecnico); setViewingTecnico(null) }}
        />
      )}
      {showRichiestaDoc && (
        <RichiestaDocModal
          tecnico={showRichiestaDoc}
          onClose={() => setShowRichiestaDoc(null)}
        />
      )}

      {tecnicoDaEliminare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="card w-full max-w-sm p-6 space-y-4">
            <h3 className="font-semibold text-slate-800">Elimina tecnico?</h3>
            <p className="text-sm text-slate-600">
              <strong>{tecnicoDaEliminare.nome} {tecnicoDaEliminare.cognome}</strong> verrà eliminato
              definitivamente insieme all'account di accesso.
            </p>
            {confirmDeleteForce && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                ⚠ Questo tecnico ha pratiche aperte assegnate. Eliminando, le pratiche verranno disassegnate. Vuoi procedere comunque?
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setTecnicoDaEliminare(null); setConfirmDeleteForce(false) }}
                className="btn-secondary flex-1"
              >
                Annulla
              </button>
              <button
                onClick={() => eliminaTecnicoMut.mutate({ id: tecnicoDaEliminare.id, force: confirmDeleteForce })}
                disabled={eliminaTecnicoMut.isPending}
                className="btn-primary flex-1"
                style={{ background: '#dc2626' }}
              >
                {eliminaTecnicoMut.isPending
                  ? 'Eliminazione...'
                  : confirmDeleteForce ? 'Elimina e disassenga' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const MENU_CATEGORIES: Record<string, string> = {
  operazioni: 'Operazioni',
  pianificazione: 'Pianificazione',
  clienti: 'Clienti',
  reporting: 'Reporting',
  sistema: 'Sistema',
}

interface MenuPerm { id: string; label: string; category: string; route: string; min_role: string; order: number; enabled: boolean }

function MenuPermissionsTab({ userId }: { userId: number }) {
  const qc = useQueryClient()
  const [localPerms, setLocalPerms] = useState<MenuPerm[]>([])
  const [saving, setSaving] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['menu-perms', userId],
    queryFn: () => menuPermissionsApi.getUserMenus(userId).then(r => r.data.permissions as MenuPerm[]),
    enabled: !!userId,
  })

  useEffect(() => { if (data) setLocalPerms(data) }, [data])

  // Usa data direttamente se localPerms non è ancora stato inizializzato dall'useEffect
  const effectivePerms = localPerms.length > 0 ? localPerms : (data ?? [])

  const toggle = (id: string) => {
    const base = localPerms.length > 0 ? localPerms : (data ?? [])
    setLocalPerms(base.map(m => m.id === id ? { ...m, enabled: !m.enabled } : m))
  }

  const toggleAll = () => {
    const base = localPerms.length > 0 ? localPerms : (data ?? [])
    const allOn = base.every(m => m.enabled)
    setLocalPerms(base.map(m => ({ ...m, enabled: !allOn })))
  }

  const save = async () => {
    setSaving(true)
    try {
      await menuPermissionsApi.updateUserMenus(userId, effectivePerms.map(m => ({ menu_id: m.id, enabled: m.enabled })))
      qc.invalidateQueries({ queryKey: ['menu-perms', userId] })
      toast.success('Permessi menu salvati')
    } catch {
      toast.error('Errore salvataggio permessi')
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) return <div className="py-8 text-center text-slate-400 text-sm">Caricamento...</div>
  if (isError) return <div className="py-8 text-center text-red-400 text-sm">Errore caricamento permessi menu</div>

  const byCategory = effectivePerms.reduce((acc, m) => {
    if (!acc[m.category]) acc[m.category] = []
    acc[m.category].push(m)
    return acc
  }, {} as Record<string, MenuPerm[]>)

  const allEnabled = effectivePerms.every(m => m.enabled)
  const someEnabled = effectivePerms.some(m => m.enabled)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
        <label className="flex items-center gap-2 cursor-pointer select-none text-sm font-semibold text-slate-700">
          <input
            type="checkbox"
            checked={allEnabled}
            ref={el => { if (el) el.indeterminate = someEnabled && !allEnabled }}
            onChange={toggleAll}
            className="rounded"
          />
          Seleziona / Deseleziona tutti
        </label>
        <button onClick={save} disabled={saving} className="btn-primary text-sm">
          {saving ? 'Salvataggio...' : 'Salva permessi'}
        </button>
      </div>

      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat}>
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">
            {MENU_CATEGORIES[cat] || cat}
          </div>
          <div className="space-y-1 pl-1">
            {items.sort((a, b) => a.order - b.order).map(m => (
              <label key={m.id} className="flex items-center gap-2.5 cursor-pointer select-none py-1 rounded hover:bg-slate-50 px-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={m.enabled}
                  onChange={() => toggle(m.id)}
                  className="rounded"
                />
                <span className="flex-1">{m.label}</span>
                <span className="text-xs text-slate-400 font-mono">{m.route}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function UserFormModal({ user, onClose, onSuccess }: { user: UserItem | null; onClose: () => void; onSuccess: () => void }) {
  const { user: currentUser } = useAuth()
  const isSuperadmin = currentUser?.role === 'superadmin'
  const isEdit = !!user
  const [tab, setTab] = useState<'dati' | 'menu'>('dati')
  const [form, setForm] = useState({ email: user?.email || '', password: '', nome: user?.nome || '', cognome: user?.cognome || '', telefono_whatsapp: user?.telefono_whatsapp || '', role: user?.role || 'tecnico', is_active: user?.is_active ?? true })
  const f = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))
  const createMut = useMutation({ mutationFn: () => authApi.createUser({ email: form.email, password: form.password, nome: form.nome, cognome: form.cognome, telefono_whatsapp: form.telefono_whatsapp || undefined, role: form.role }), onSuccess, onError: () => toast.error('Errore creazione (email duplicata?)') })
  const updateMut = useMutation({ mutationFn: () => { const d: any = { email: form.email, nome: form.nome, cognome: form.cognome, telefono_whatsapp: form.telefono_whatsapp || null, role: form.role, is_active: form.is_active }; if (form.password) d.password = form.password; return authApi.updateUser(user!.id, d) }, onSuccess, onError: () => toast.error('Errore aggiornamento') })
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); isEdit ? updateMut.mutate() : createMut.mutate() }
  const isPending = createMut.isPending || updateMut.isPending

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="p-6 border-b flex items-center gap-2 flex-shrink-0">
          <Shield size={20} className="text-blue-600" />
          <h2 className="text-lg font-semibold">{isEdit ? 'Modifica Utente' : 'Nuovo Utente'}</h2>
        </div>

        {isEdit && isSuperadmin && (
          <div className="flex gap-1 px-6 pt-4 flex-shrink-0">
            {(['dati', 'menu'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={clsx('px-4 py-1.5 text-sm font-medium rounded-t border-b-2 transition-colors',
                  tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700')}>
                {t === 'dati' ? 'Dati Utente' : 'Permessi Menu'}
              </button>
            ))}
          </div>
        )}

        <div className="overflow-y-auto flex-1">
          {tab === 'dati' && (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">Nome</label><input className="input" value={form.nome} onChange={(e) => f('nome', e.target.value)} /></div>
                <div><label className="label">Cognome</label><input className="input" value={form.cognome} onChange={(e) => f('cognome', e.target.value)} /></div>
              </div>
              <div><label className="label">Email *</label><input type="email" className="input" value={form.email} onChange={(e) => f('email', e.target.value)} required /></div>
              <div><label className="label">{isEdit ? 'Nuova Password (vuoto = invariata)' : 'Password *'}</label><input type="password" className="input" value={form.password} onChange={(e) => f('password', e.target.value)} required={!isEdit} /></div>
              <div><label className="label">WhatsApp (es. 393331234567)</label><input className="input" placeholder="39XXXXXXXXXX" value={form.telefono_whatsapp} onChange={(e) => f('telefono_whatsapp', e.target.value)} /></div>
              <div><label className="label">Ruolo *</label><select className="select" value={form.role} onChange={(e) => f('role', e.target.value)}>{ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
              {isEdit && <div className="flex items-center gap-2"><input type="checkbox" id="is_active" checked={form.is_active} onChange={(e) => f('is_active', e.target.checked)} /><label htmlFor="is_active" className="text-sm text-slate-700">Account attivo</label></div>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
                <button type="submit" disabled={isPending} className="btn-primary flex-1">{isPending ? 'Salvataggio...' : isEdit ? 'Salva Modifiche' : 'Crea Utente'}</button>
              </div>
            </form>
          )}

          {tab === 'menu' && isEdit && isSuperadmin && (
            <div className="p-6">
              <p className="text-xs text-slate-500 mb-4">
                Seleziona i menu visibili a <strong>{user.nome} {user.cognome}</strong>. La sidebar mostrerà solo i menu abilitati.
              </p>
              <MenuPermissionsTab userId={user.id} />
            </div>
          )}
        </div>

        {tab === 'menu' && (
          <div className="p-6 border-t flex-shrink-0">
            <button onClick={onClose} className="btn-secondary w-full">Chiudi</button>
          </div>
        )}
      </div>
    </div>
  )
}

function CreateTecnicoModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({ nome: '', cognome: '', codice_fiscale: '', telefono: '', telefono_whatsapp: '', email: '', password: '', categoria_contrattuale: 'dipendente_diretto', livello_accesso: 'base', zona_primaria: '', veicolo_targa: '', patente: 'B', email_operativa: '', costo_orario: null as number | null, tariffa_rfi_codice: '' })
  const f = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))
  const { data: mezziListC = [] } = useQuery({
    queryKey: ['mezzi-targhe'],
    queryFn: () => getMezzi(true).then(r => r.data),
    staleTime: 60_000,
  })
  const { data: listinoManodoperaC = [] } = useListinoManodoperaRfi()
  const mutation = useMutation({
    mutationFn: (data: typeof form) => {
      const payload = { ...data }
      if (!payload.veicolo_targa) delete (payload as any).veicolo_targa
      if (!payload.codice_fiscale) delete (payload as any).codice_fiscale
      if (!payload.tariffa_rfi_codice) delete (payload as any).tariffa_rfi_codice
      return tecniciApi.create(payload)
    },
    onSuccess,
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Errore creazione tecnico'),
  })
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b"><h2 className="text-lg font-semibold">Nuovo Tecnico</h2></div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(form) }} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Nome *</label><input className="input" value={form.nome} onChange={(e) => f('nome', e.target.value)} required /></div>
            <div><label className="label">Cognome *</label><input className="input" value={form.cognome} onChange={(e) => f('cognome', e.target.value)} required /></div>
            <div><label className="label">CF</label><input className="input uppercase" value={form.codice_fiscale} onChange={(e) => f('codice_fiscale', e.target.value.toUpperCase())} /></div>
            <div><label className="label">Telefono</label><input className="input" value={form.telefono} onChange={(e) => f('telefono', e.target.value)} /></div>
            <div><label className="label">WhatsApp (es. 393331234567)</label><input className="input" placeholder="393XXXXXXXXX" value={form.telefono_whatsapp} onChange={(e) => f('telefono_whatsapp', e.target.value)} /></div>
            <div><label className="label">Email *</label><input type="email" className="input" value={form.email} onChange={(e) => f('email', e.target.value)} required /></div>
            <div><label className="label">Password *</label><input type="password" className="input" value={form.password} onChange={(e) => f('password', e.target.value)} required /></div>
            <div><label className="label">Categoria</label><select className="select" value={form.categoria_contrattuale} onChange={(e) => f('categoria_contrattuale', e.target.value)}>{Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <div><label className="label">Livello</label><select className="select" value={form.livello_accesso} onChange={(e) => f('livello_accesso', e.target.value)}>{Object.entries(LIVELLO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            <div><label className="label">Zona</label><input className="input" value={form.zona_primaria} onChange={(e) => f('zona_primaria', e.target.value)} /></div>
            <div>
              <label className="label">Targa</label>
              <input
                className="input uppercase"
                list="targhe-datalist-utenti-create"
                value={form.veicolo_targa}
                onChange={(e) => f('veicolo_targa', e.target.value.toUpperCase())}
                placeholder="ES: AB123CD"
              />
              <datalist id="targhe-datalist-utenti-create">
                {mezziListC.map(m => <option key={m.id} value={m.targa} />)}
              </datalist>
              {form.veicolo_targa && !mezziListC.find(m => m.targa === form.veicolo_targa) && (
                <p style={{ color: 'var(--mac-orange)', fontSize: '0.72rem', marginTop: '0.2rem' }}>⏳ Veicolo non ancora in Fleet</p>
              )}
            </div>
            <div>
              <label className="label">Costo orario (€/h)</label>
              <input type="number" min="0" step="0.01" className="input" placeholder="es. 25.00" value={form.costo_orario ?? ''} onChange={e => setForm(p => ({ ...p, costo_orario: e.target.value ? parseFloat(e.target.value) : null }))} />
            </div>
            <div>
              <label className="label">Tariffa manodopera RFI</label>
              <select className="select" value={form.tariffa_rfi_codice} onChange={e => f('tariffa_rfi_codice', e.target.value)}>
                <option value="">— nessuna —</option>
                {listinoManodoperaC.map(v => (
                  <option key={v.tariffa} value={v.tariffa}>{v.descrizione} ({v.prezzo.toFixed(2)} €/h)</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary flex-1">{mutation.isPending ? 'Creazione...' : 'Crea Tecnico'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EditTecnicoModal({ tecnico, onClose, onSuccess }: { tecnico: Tecnico; onClose: () => void; onSuccess: () => void }) {
  const [section, setSection] = useState<'anagrafica' | 'scadenze' | 'corsi' | 'beni'>('anagrafica')

  // La riga passata in `tecnico` arriva dalla lista paginata, che usa uno schema
  // di risposta ridotto (senza iban/banca/costo_orario/patente/codice_fiscale/...).
  // Carichiamo il dettaglio completo all'apertura del modal per non mostrare
  // sempre vuoti quei campi anche quando nel DB sono valorizzati.
  const { data: dettaglio } = useQuery({
    queryKey: ['tecnico-dettaglio', tecnico.id],
    queryFn: () => tecniciApi.get(tecnico.id).then(r => r.data),
  })
  const t = (dettaglio as any) || tecnico

  const [form, setForm] = useState({
    nome: tecnico.nome || '',
    cognome: tecnico.cognome || '',
    telefono: tecnico.telefono || '',
    telefono_whatsapp: (tecnico as any).telefono_whatsapp || '',
    codice_fiscale: (tecnico as any).codice_fiscale || '',
    zona_primaria: tecnico.zona_primaria || '',
    zona_secondaria: (tecnico as any).zona_secondaria || '',
    veicolo_targa: tecnico.veicolo_targa || '',
    patente: (tecnico as any).patente || 'B',
    iban: tecnico.iban || '',
    banca: tecnico.banca || '',
    categoria_contrattuale: tecnico.categoria_contrattuale || 'dipendente_diretto',
    livello_accesso: tecnico.livello_accesso || 'base',
    is_active: tecnico.is_active ?? true,
    costo_orario: (tecnico as any).costo_orario ?? null,
    tariffa_rfi_codice: (tecnico as any).tariffa_rfi_codice || '',
    // scadenze
    scadenza_patente: (tecnico as any).scadenza_patente || '',
    ci_scadenza: (tecnico as any).ci_scadenza || '',
    idoneita_medica_scadenza: (tecnico as any).idoneita_medica_scadenza || '',
    formazione_81_scadenza: (tecnico as any).formazione_81_scadenza || '',
  })
  const [corsi, setCorsi] = useState<any[]>((tecnico as any).storico_formazione || [])
  const [newCorso, setNewCorso] = useState({ corso: '', data: '', ente: '', ore: '', data_scadenza: '' })

  useEffect(() => {
    if (!dettaglio) return
    setForm({
      nome: t.nome || '',
      cognome: t.cognome || '',
      telefono: t.telefono || '',
      telefono_whatsapp: t.telefono_whatsapp || '',
      codice_fiscale: t.codice_fiscale || '',
      zona_primaria: t.zona_primaria || '',
      zona_secondaria: t.zona_secondaria || '',
      veicolo_targa: t.veicolo_targa || '',
      patente: t.patente || 'B',
      iban: t.iban || '',
      banca: t.banca || '',
      categoria_contrattuale: t.categoria_contrattuale || 'dipendente_diretto',
      livello_accesso: t.livello_accesso || 'base',
      is_active: t.is_active ?? true,
      costo_orario: t.costo_orario ?? null,
      tariffa_rfi_codice: t.tariffa_rfi_codice || '',
      scadenza_patente: t.scadenza_patente || '',
      ci_scadenza: t.ci_scadenza || '',
      idoneita_medica_scadenza: t.idoneita_medica_scadenza || '',
      formazione_81_scadenza: t.formazione_81_scadenza || '',
    })
    setCorsi(t.storico_formazione || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dettaglio])

  const f = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))
  const { data: listinoManodoperaE = [] } = useListinoManodoperaRfi()

  const mutation = useMutation({
    mutationFn: () => tecniciApi.update(tecnico.id, {
      ...form,
      storico_formazione: corsi,
      scadenza_patente: form.scadenza_patente || null,
      ci_scadenza: form.ci_scadenza || null,
      idoneita_medica_scadenza: form.idoneita_medica_scadenza || null,
      formazione_81_scadenza: form.formazione_81_scadenza || null,
      tariffa_rfi_codice: form.tariffa_rfi_codice || null,
    }),
    onSuccess,
    onError: () => toast.error('Errore aggiornamento'),
  })

  const { data: mezziListE = [] } = useQuery({
    queryKey: ['mezzi-targhe'],
    queryFn: () => getMezzi(true).then(r => r.data),
    staleTime: 60_000,
  })

  const addCorso = () => {
    if (!newCorso.corso) return
    setCorsi(c => [...c, { ...newCorso, ore: Number(newCorso.ore) || 0 }])
    setNewCorso({ corso: '', data: '', ente: '', ore: '', data_scadenza: '' })
  }

  const SECTIONS = [
    { key: 'anagrafica', label: 'Anagrafica' },
    { key: 'scadenze',   label: 'Scadenze Documenti' },
    { key: 'corsi',      label: 'Corsi / Formazione' },
    { key: 'beni',       label: '🔧 Beni e Attrezzature' },
  ] as const

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Modifica Tecnico — {tecnico.nome} {tecnico.cognome}</h2>
          <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 px-5 pt-2">
          {SECTIONS.map(s => (
            <button key={s.key} onClick={() => setSection(s.key)}
              className={clsx('px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                section === s.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700')}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">

          {section === 'anagrafica' && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="label">Nome</label><input className="input" value={form.nome} onChange={e => f('nome', e.target.value)} /></div>
              <div><label className="label">Cognome</label><input className="input" value={form.cognome} onChange={e => f('cognome', e.target.value)} /></div>
              <div><label className="label">Codice Fiscale</label><input className="input uppercase" value={form.codice_fiscale} onChange={e => f('codice_fiscale', e.target.value.toUpperCase())} /></div>
              <div><label className="label">Telefono</label><input className="input" value={form.telefono} onChange={e => f('telefono', e.target.value)} /></div>
              <div><label className="label">WhatsApp (es. 393331234567)</label><input className="input" placeholder="393XXXXXXXXX" value={form.telefono_whatsapp} onChange={e => f('telefono_whatsapp', e.target.value)} /></div>
              <div><label className="label">Categoria</label>
                <select className="select" value={form.categoria_contrattuale} onChange={e => f('categoria_contrattuale', e.target.value)}>
                  {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><label className="label">Livello</label>
                <select className="select" value={form.livello_accesso} onChange={e => f('livello_accesso', e.target.value)}>
                  {Object.entries(LIVELLO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><label className="label">Zona Primaria</label><input className="input" value={form.zona_primaria} onChange={e => f('zona_primaria', e.target.value)} /></div>
              <div><label className="label">Zona Secondaria</label><input className="input" value={form.zona_secondaria} onChange={e => f('zona_secondaria', e.target.value)} /></div>
              <div>
                <label className="label">Targa Veicolo</label>
                <input
                  className="input uppercase"
                  list="targhe-datalist-utenti-edit"
                  value={form.veicolo_targa}
                  onChange={e => f('veicolo_targa', e.target.value.toUpperCase())}
                  placeholder="ES: AB123CD"
                />
                <datalist id="targhe-datalist-utenti-edit">
                  {mezziListE.map((m: any) => <option key={m.id} value={m.targa} />)}
                </datalist>
                {form.veicolo_targa && !mezziListE.find((m: any) => m.targa === form.veicolo_targa) && (
                  <p style={{ color: 'var(--mac-orange)', fontSize: '0.72rem', marginTop: '0.2rem' }}>⏳ Veicolo non ancora in Fleet</p>
                )}
              </div>
              <div><label className="label">Patente</label><input className="input" value={form.patente} onChange={e => f('patente', e.target.value)} /></div>
              <div>
                <label className="label">Costo orario (€/h)</label>
                <input type="number" min="0" step="0.01" className="input" placeholder="es. 25.00" value={form.costo_orario ?? ''} onChange={e => f('costo_orario', e.target.value ? parseFloat(e.target.value) : null)} />
              </div>
              <div>
                <label className="label">Tariffa manodopera RFI</label>
                <select className="select" value={form.tariffa_rfi_codice} onChange={e => f('tariffa_rfi_codice', e.target.value)}>
                  <option value="">— nessuna —</option>
                  {listinoManodoperaE.map(v => (
                    <option key={v.tariffa} value={v.tariffa}>{v.descrizione} ({v.prezzo.toFixed(2)} €/h)</option>
                  ))}
                </select>
              </div>
              <div><label className="label">Banca</label><input className="input" value={form.banca} onChange={e => f('banca', e.target.value)} /></div>
              <div>
                <label className="label">IBAN</label>
                <input className="input uppercase font-mono" placeholder="IT..." value={form.iban} onChange={e => f('iban', e.target.value.toUpperCase())} />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="is_active_tec" checked={form.is_active} onChange={e => f('is_active', e.target.checked)} />
                <label htmlFor="is_active_tec" className="text-sm text-slate-700">Tecnico attivo</label>
              </div>
            </div>
          )}

          {section === 'scadenze' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">Le scadenze imminenti (≤30 gg) generano allerta nel pannello Utenti. Puoi allegare foto e file per ogni documento.</p>
              <div className="grid grid-cols-1 gap-5">
                {([
                  { key: 'scadenza_patente',        label: 'Patente',              tipo: 'patente' },
                  { key: 'ci_scadenza',              label: "Carta d'identità",     tipo: 'ci' },
                  { key: 'idoneita_medica_scadenza', label: 'Idoneità Medica',      tipo: 'idoneita_medica' },
                  { key: 'formazione_81_scadenza',   label: 'Formazione D.Lgs 81', tipo: 'formazione_81' },
                ] as { key: string; label: string; tipo: string }[]).map(({ key, label, tipo }) => (
                  <div key={key} className="rounded-xl border border-slate-200 p-4 bg-white space-y-3">
                    <div className="flex items-center gap-3">
                      <label className="label mb-0 shrink-0 w-48 font-semibold">{label}</label>
                      <input type="date" className="input flex-1"
                        value={(form as any)[key] || ''}
                        onChange={e => f(key, e.target.value)}
                      />
                      {(form as any)[key] && (
                        <button onClick={() => f(key, '')} className="btn-ghost px-2 text-slate-400 text-xs">✕</button>
                      )}
                    </div>
                    <div className="pl-2 border-l-2 border-slate-100">
                      <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Paperclip size={11} /> Allegati <span className="text-slate-300">(max 2)</span></p>
                      <AllegatiPanel tecnicoId={tecnico.id} tipo={tipo} maxFiles={2} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {section === 'corsi' && (
            <div className="space-y-4">
              {/* Aggiungi corso */}
              <div className="card p-4 bg-slate-50">
                <p className="text-xs font-semibold text-slate-600 mb-3">Aggiungi corso</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><input className="input" placeholder="Nome corso *" value={newCorso.corso} onChange={e => setNewCorso(p => ({ ...p, corso: e.target.value }))} /></div>
                  <div>
                    <label className="label text-xs">Data conseguimento</label>
                    <input type="date" className="input" value={newCorso.data} onChange={e => setNewCorso(p => ({ ...p, data: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label text-xs">Data scadenza</label>
                    <input type="date" className="input" value={newCorso.data_scadenza} onChange={e => setNewCorso(p => ({ ...p, data_scadenza: e.target.value }))} />
                  </div>
                  <input className="input" placeholder="Ente erogatore" value={newCorso.ente} onChange={e => setNewCorso(p => ({ ...p, ente: e.target.value }))} />
                  <input type="number" className="input" placeholder="Ore" value={newCorso.ore} onChange={e => setNewCorso(p => ({ ...p, ore: e.target.value }))} />
                  <div className="col-span-2">
                    <button type="button" onClick={addCorso} className="btn-primary text-sm w-full">+ Aggiungi</button>
                  </div>
                </div>
              </div>

              {/* Lista corsi */}
              {corsi.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nessun corso registrato</p>
              ) : (
                <div className="space-y-2">
                  {corsi.map((c, i) => {
                    let scadBadge = null
                    if (c.data_scadenza) {
                      const giorni = Math.ceil((new Date(c.data_scadenza).getTime() - Date.now()) / 86400000)
                      if (giorni < 0) scadBadge = <span style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 99, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>SCADUTO</span>
                      else if (giorni <= 30) scadBadge = <span style={{ background: '#fef9c3', color: '#92400e', borderRadius: 99, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>Scade tra {giorni}gg</span>
                      else scadBadge = <span style={{ background: '#f0fdf4', color: '#15803d', borderRadius: 99, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>Valido</span>
                    }
                    return (
                      <div key={i} className="rounded-lg border border-slate-100 bg-white text-sm overflow-hidden">
                        <div className="flex items-center gap-3 p-3">
                          <Calendar size={14} className="text-slate-400 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-800 flex items-center gap-2 flex-wrap">
                              {c.corso}
                              {scadBadge}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              {c.data && `Conseguito: ${c.data}`}
                              {c.data_scadenza && ` · Scade: ${c.data_scadenza}`}
                              {c.ente && ` · ${c.ente}`}
                              {c.ore > 0 && ` · ${c.ore}h`}
                            </div>
                          </div>
                          <button onClick={() => setCorsi(cs => cs.filter((_, j) => j !== i))} className="btn-ghost px-2 py-1 text-red-500 text-xs shrink-0">✕</button>
                        </div>
                        <div className="px-4 pb-3 border-t border-slate-50 pt-2 bg-slate-50/50">
                          <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Paperclip size={11} /> Allegati attestato</p>
                          <AllegatiPanel tecnicoId={tecnico.id} tipo="corso" corsoRef={c.corso} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {section === 'beni' && (
            <BeniAttrezzature
              tecnicoId={tecnico.id}
              tecnicoNome={`${tecnico.nome} ${tecnico.cognome}`}
            />
          )}

        </div>

        <div className="p-5 border-t flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
          {section !== 'beni' && (
            <button onClick={() => mutation.mutate()} disabled={mutation.isPending} className="btn-primary flex-1">
              {mutation.isPending ? 'Salvataggio...' : 'Salva Modifiche'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── AllegatiPanel ────────────────────────────────────────────────────────────
interface AllegatoItem { id: number; nome_file: string; dimensione?: number; mime_type?: string; created_at?: string }

function AllegatiPanel({ tecnicoId, tipo, corsoRef, maxFiles = 1 }: {
  tecnicoId: number; tipo: string; corsoRef?: string; maxFiles?: number
}) {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const qc = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [pendingSlot, setPendingSlot] = React.useState<number | null>(null)

  const queryKey = ['allegati', tecnicoId, tipo, corsoRef ?? '']

  const { data: allegati = [], isLoading } = useQuery<AllegatoItem[]>({
    queryKey,
    queryFn: () => allegatiTecniciApi.list(tecnicoId, tipo, corsoRef).then(r => r.data),
    enabled: !!tecnicoId,
  })

  const uploadMut = useMutation({
    mutationFn: (file: File) => allegatiTecniciApi.upload(tecnicoId, file, tipo, corsoRef),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setPendingSlot(null); toast.success('File caricato') },
    onError: (e: any) => { setPendingSlot(null); toast.error(e.response?.data?.detail || 'Errore upload') },
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => allegatiTecniciApi.delete(tecnicoId, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); toast.success('File eliminato') },
    onError: () => toast.error('Errore eliminazione'),
  })

  const handleDownload = async (a: AllegatoItem) => {
    try {
      const res = await allegatiTecniciApi.download(tecnicoId, a.id)
      const url = URL.createObjectURL(new Blob([res.data], { type: a.mime_type || 'application/octet-stream' }))
      const link = document.createElement('a')
      link.href = url
      link.download = a.nome_file
      link.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Errore download') }
  }

  const fmtSize = (b?: number) => !b ? '' : b < 1024 ? `${b}B` : b < 1048576 ? `${(b/1024).toFixed(0)}KB` : `${(b/1048576).toFixed(1)}MB`

  const handleSlotUpload = (slotIdx: number) => {
    setPendingSlot(slotIdx)
    inputRef.current?.click()
  }

  if (maxFiles <= 1) {
    // Comportamento originale: lista libera + pulsante aggiungi
    return (
      <div className="mt-2 space-y-1.5">
        {ConfirmModal}
        {isLoading ? (
          <p className="text-xs text-slate-400">Caricamento...</p>
        ) : allegati.length === 0 ? (
          <p className="text-xs text-slate-400 italic">Nessun file caricato</p>
        ) : (
          allegati.map(a => (
            <div key={a.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-100 text-xs">
              <FileText size={13} className="text-blue-500 shrink-0" />
              <span className="flex-1 truncate text-slate-700 font-medium" title={a.nome_file}>{a.nome_file}</span>
              {a.dimensione && <span className="text-slate-400 shrink-0">{fmtSize(a.dimensione)}</span>}
              <button onClick={() => handleDownload(a)} title="Scarica" className="p-1 hover:text-blue-600 text-slate-400 transition-colors">
                <Download size={13} />
              </button>
              <button
                onClick={() => confirmDelete(`Eliminare "${a.nome_file}"?`, () => deleteMut.mutate(a.id))}
                title="Elimina" className="p-1 hover:text-red-500 text-slate-400 transition-colors"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))
        )}
        <div>
          <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) uploadMut.mutate(f); e.target.value = '' }}
          />
          <button onClick={() => inputRef.current?.click()} disabled={uploadMut.isPending}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors py-1"
          >
            <Upload size={12} />
            {uploadMut.isPending ? 'Caricamento...' : 'Carica file'}
          </button>
        </div>
      </div>
    )
  }

  // UI a slot fissi (es. maxFiles=2)
  return (
    <div className="mt-1 space-y-1.5">
      {ConfirmModal}
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) uploadMut.mutate(f); e.target.value = '' }}
      />
      {isLoading ? (
        <p className="text-xs text-slate-400">Caricamento...</p>
      ) : (
        Array.from({ length: maxFiles }).map((_, slotIdx) => {
          const allegato = allegati[slotIdx]
          const isUploading = uploadMut.isPending && pendingSlot === slotIdx
          return (
            <div key={slotIdx} className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-slate-400 w-3 shrink-0">{slotIdx + 1}</span>
              {allegato ? (
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-100 text-xs flex-1 min-w-0">
                  <FileText size={12} className="text-blue-500 shrink-0" />
                  <span className="flex-1 truncate text-slate-700 font-medium" title={allegato.nome_file}>{allegato.nome_file}</span>
                  {allegato.dimensione && <span className="text-slate-400 shrink-0 text-[10px]">{fmtSize(allegato.dimensione)}</span>}
                  <button onClick={() => handleDownload(allegato)} title="Scarica" className="p-0.5 hover:text-blue-600 text-slate-400 transition-colors shrink-0">
                    <Download size={12} />
                  </button>
                  <button
                    onClick={() => confirmDelete(`Eliminare "${allegato.nome_file}"?`, () => deleteMut.mutate(allegato.id))}
                    title="Elimina" className="p-0.5 hover:text-red-500 text-slate-400 transition-colors shrink-0"
                    disabled={deleteMut.isPending}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleSlotUpload(slotIdx)}
                  disabled={uploadMut.isPending}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-slate-200 text-xs text-slate-400 hover:border-blue-300 hover:text-blue-600 transition-colors flex-1"
                >
                  {isUploading ? (
                    <><span className="animate-spin inline-block w-3 h-3 border border-blue-400 border-t-transparent rounded-full" /> Caricamento...</>
                  ) : (
                    <><Upload size={11} /> Carica allegato</>
                  )}
                </button>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}

// ─── Badge stato verbale ─────────────────────────────────────────────────────
function StatoVerbale({ stato }: { stato: string }) {
  const cfg: Record<string, { bg: string; color: string; label: string; dot: string }> = {
    bozza:   { bg: '#f3f4f6', color: '#6b7280', label: 'Bozza',              dot: '#9ca3af' },
    inviata: { bg: '#fff7ed', color: '#c2410c', label: 'In attesa firma',     dot: '#f97316' },
    firmata: { bg: '#f0fdf4', color: '#166534', label: 'Firmata',             dot: '#22c55e' },
  }
  const c = cfg[stato] || cfg.bozza
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: c.bg, color: c.color, padding: '2px 8px',
      borderRadius: 99, fontSize: 11, fontWeight: 600,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.dot, display: 'inline-block' }} />
      {c.label}
    </span>
  )
}

// ─── Campi strutturati automezzo ─────────────────────────────────────────────
const CAMPI_AUTOMEZZO = [
  { key: 'modello',            label: 'Modello' },
  { key: 'targa',              label: 'Targa' },
  { key: 'stato_uso',          label: 'Stato di uso' },
  { key: 'kk_uscita',         label: 'KK all\'uscita' },
  { key: 'carta_carburante_nr', label: 'Carta carburante N°' },
  { key: 'pin',                label: 'PIN' },
  { key: 'telepass_modello',   label: 'Telepass modello' },
]

const PRESET_BENI = [
  {
    categoria: 'automezzo',
    descrizione: 'Automezzo Aziendale',
    seriale: '', quantita: 1, unita: 'pz', note: '',
    campi_extra: { modello: '', targa: '', stato_uso: '', kk_uscita: '', carta_carburante_nr: '', pin: '', telepass_modello: '' },
  },
  {
    categoria: 'attrezzatura',
    descrizione: 'Scaletta "Peppina"',
    seriale: '', quantita: 1, unita: 'pz', note: '',
    campi_extra: {},
  },
]

const CATEGORIE = [
  { value: 'automezzo',    label: '🚗 Automezzo' },
  { value: 'attrezzatura', label: '🔧 Attrezzatura' },
  { value: 'dpi',          label: '🦺 DPI' },
  { value: 'altro',        label: '📦 Altro' },
]

// ─── TecnicoViewModal (sola lettura) ───────────────────────────────────────────
function TecnicoViewModal({
  tecnico, onClose, onEdit, canEdit,
}: {
  tecnico: Tecnico; onClose: () => void; onEdit: () => void; canEdit: boolean
}) {
  const qc = useQueryClient()
  const fotoInputRef = useRef<HTMLInputElement>(null)
  const [cropSrc, setCropSrc] = useState<string | null>(null)

  const { data: dettaglio } = useQuery({
    queryKey: ['tecnico-dettaglio', tecnico.id],
    queryFn: () => tecniciApi.get(tecnico.id).then(r => r.data),
  })
  const t: any = dettaglio || tecnico

  const invalidateFoto = () => {
    qc.invalidateQueries({ queryKey: ['tecnici'] })
    qc.invalidateQueries({ queryKey: ['tecnico-dettaglio', tecnico.id] })
  }

  const uploadFotoMut = useMutation({
    mutationFn: (file: File | Blob) => tecniciApi.uploadFoto(tecnico.id, file as File),
    onSuccess: () => { invalidateFoto(); toast.success('Foto caricata') },
    onError: () => toast.error('Errore caricamento foto'),
  })
  const deleteFotoMut = useMutation({
    mutationFn: () => tecniciApi.deleteFoto(tecnico.id),
    onSuccess: () => { invalidateFoto(); toast.success('Foto rimossa') },
    onError: () => toast.error('Errore rimozione foto'),
  })

  const handleFileSelected = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => setCropSrc(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleRicroppa = async () => {
    try {
      const r = await tecniciApi.getFoto(tecnico.id)
      const reader = new FileReader()
      reader.onload = () => setCropSrc(reader.result as string)
      reader.readAsDataURL(r.data)
    } catch {
      toast.error('Impossibile caricare la foto attuale')
    }
  }

  const handleCropConfirm = (blob: Blob) => {
    setCropSrc(null)
    uploadFotoMut.mutate(blob)
  }

  const row = (label: string, value: any) => (
    <div>
      <div className="text-xs text-slate-400 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-slate-800">{value || value === 0 ? value : <span className="text-slate-300">—</span>}</div>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Anagrafica Tecnico</h2>
          <div className="flex items-center gap-2">
            {canEdit && (
              <button onClick={onEdit} className="btn-primary text-sm flex items-center gap-1.5">
                <Pencil size={14} /> Modifica
              </button>
            )}
            <button onClick={onClose} className="btn-ghost p-1 text-slate-400">✕</button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-6">
          {/* Foto + identità */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <TecnicoAvatar tecnico={t} size={88} />
              {canEdit && (
                <button
                  onClick={() => fotoInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 bg-white border border-slate-200 rounded-full p-1.5 shadow-sm hover:bg-slate-50"
                  title="Carica foto"
                >
                  <Upload size={12} className="text-slate-600" />
                </button>
              )}
              <input
                ref={fotoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelected(f); e.target.value = '' }}
              />
            </div>
            <div className="flex-1">
              <div className="text-xl font-semibold text-slate-900">{t.nome} {t.cognome}</div>
              <div className="text-sm text-slate-500">{LIVELLO_LABELS[t.livello_accesso] || t.livello_accesso} · {CAT_LABELS[t.categoria_contrattuale] || t.categoria_contrattuale}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className={clsx('inline-flex items-center gap-1 text-xs', t.is_active ? 'text-green-600' : 'text-slate-400')}>
                  <span className={clsx('w-1.5 h-1.5 rounded-full', t.is_active ? 'bg-green-500' : 'bg-slate-300')} />
                  {t.is_active ? 'Attivo' : 'Non attivo'}
                </span>
                {t.foto_source === 'documento' && <span className="text-xs text-slate-400">· foto da documento caricato</span>}
              </div>
              {canEdit && t.foto_source && (
                <div className="flex items-center gap-3 mt-1">
                  <button onClick={handleRicroppa} className="text-xs text-slate-500 hover:underline flex items-center gap-1">
                    <Crop size={11} /> Ritaglia di nuovo
                  </button>
                  {t.foto_source === 'manuale' && (
                    <button onClick={() => deleteFotoMut.mutate()} className="text-xs text-red-500 hover:underline">
                      Rimuovi foto caricata
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {cropSrc && (
            <PhotoCropModal
              imageSrc={cropSrc}
              onCancel={() => setCropSrc(null)}
              onConfirm={handleCropConfirm}
            />
          )}

          {/* Anagrafica */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-100">Dati Anagrafici</h3>
            <div className="grid grid-cols-2 gap-4">
              {row('Codice Fiscale', t.codice_fiscale)}
              {row('Data di nascita', t.data_nascita)}
              {row('Telefono', t.telefono)}
              {row('WhatsApp', t.telefono_whatsapp)}
              {row('Zona Primaria', t.zona_primaria)}
              {row('Zona Secondaria', t.zona_secondaria)}
              {row('Targa Veicolo', t.veicolo_targa)}
              {row('Patente', t.patente)}
              {row('Costo orario', t.costo_orario ? `€ ${Number(t.costo_orario).toFixed(2)}/h` : null)}
              {row('Banca', t.banca)}
              {row('IBAN', t.iban)}
            </div>
          </div>

          {/* Scadenze */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-100">Scadenze Documenti</h3>
            <div className="grid grid-cols-2 gap-4">
              {row('Patente', t.scadenza_patente)}
              {row("Carta d'identità", t.ci_scadenza)}
              {row('Idoneità Medica', t.idoneita_medica_scadenza)}
              {row('Formazione D.Lgs 81', t.formazione_81_scadenza)}
            </div>
          </div>

          {/* Corsi */}
          {!!(t.storico_formazione && t.storico_formazione.length) && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-100">Corsi / Formazione</h3>
              <div className="space-y-2">
                {t.storico_formazione.map((c: any, i: number) => (
                  <div key={i} className="text-sm text-slate-700 flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <span>{c.corso}</span>
                    <span className="text-xs text-slate-400">{c.ente} · {c.data}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {t.note && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2 pb-2 border-b border-slate-100">Note</h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{t.note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Beni e Attrezzature ──────────────────────────────────────────────────────
// ─── RichiestaDocModal ────────────────────────────────────────────────────────
function RichiestaDocModal({ tecnico, onClose }: { tecnico: Tecnico; onClose: () => void }) {
  const qc = useQueryClient()
  const [tipi, setTipi] = useState<Set<string>>(new Set(['patente']))
  const [note, setNote] = useState('')

  const { data: pendentiData } = useQuery({
    queryKey: ['richieste-pendenti', tecnico.id],
    queryFn: () => richiesteDocApi.pendenti(tecnico.id).then(r => r.data),
  })
  const pendenti: string[] = pendentiData?.richieste?.map((r: any) => r.tipo) ?? []

  const toggleTipo = (t: string) => {
    if (pendenti.includes(t)) return
    setTipi(prev => { const s = new Set(prev); s.has(t) ? s.delete(t) : s.add(t); return s })
  }

  const tipiDisponibili = Array.from(tipi).filter(t => !pendenti.includes(t))

  const invioMut = useMutation({
    mutationFn: () => richiesteDocApi.richiedi(tecnico.id, tipiDisponibili, note.trim() || undefined),
    onSuccess: () => {
      toast.success('Richiesta inviata al tecnico!')
      qc.invalidateQueries({ queryKey: ['richieste-pendenti', tecnico.id] })
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore invio richiesta'),
  })

  const resetMut = useMutation({
    mutationFn: () => richiesteDocApi.reset(tecnico.id),
    onSuccess: (r) => {
      toast.success(`Reset completato: ${r.data.eliminati} richiesta/e eliminata/e`)
      qc.invalidateQueries({ queryKey: ['richieste-pendenti', tecnico.id] })
    },
    onError: () => toast.error('Errore reset richieste'),
  })

  const OPZIONI = [
    { value: 'patente', label: 'Patente (fronte e retro)' },
    { value: 'ci',      label: "Carta d'identità (fronte e retro)" },
  ] as const

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-sm p-6 space-y-4">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <FileUp size={17} className="text-blue-600" />
          Richiedi documento — {tecnico.nome} {tecnico.cognome}
        </h2>

        {pendenti.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
            <p className="text-xs font-medium text-amber-700">Richieste già pendenti:</p>
            <div className="flex flex-wrap gap-1">
              {pendentiData.richieste.map((r: any) => (
                <span key={r.id} className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{r.tipo_label}</span>
              ))}
            </div>
            <button
              onClick={() => resetMut.mutate()}
              disabled={resetMut.isPending}
              className="text-xs text-red-600 hover:text-red-800 font-medium underline underline-offset-2"
            >
              {resetMut.isPending ? 'Reset in corso...' : 'Elimina tutte le richieste pendenti'}
            </button>
          </div>
        )}

        <div className="space-y-2">
          <label className="label">Documenti da richiedere</label>
          {OPZIONI.map(opt => {
            const isPendente = pendenti.includes(opt.value)
            return (
              <label
                key={opt.value}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${isPendente ? 'opacity-50 cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:bg-slate-50'}`}
                style={{ borderColor: isPendente ? '#e5e7eb' : tipi.has(opt.value) ? '#1E40AF' : '#e5e7eb', background: !isPendente && tipi.has(opt.value) ? '#eff6ff' : '' }}
              >
                <input
                  type="checkbox"
                  checked={isPendente || tipi.has(opt.value)}
                  disabled={isPendente}
                  onChange={() => toggleTipo(opt.value)}
                  className="accent-blue-600 w-4 h-4"
                />
                <span className="text-sm font-medium">{opt.label}</span>
                {isPendente && <span className="ml-auto text-xs text-amber-600 font-medium">pendente</span>}
              </label>
            )
          })}
        </div>

        <div>
          <label className="label">Note per il tecnico (opzionale)</label>
          <textarea
            className="input" rows={2}
            placeholder="Es: Aggiorna entro fine settimana"
            value={note} onChange={e => setNote(e.target.value)}
          />
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="btn-secondary flex-1">Annulla</button>
          <button
            onClick={() => invioMut.mutate()}
            disabled={invioMut.isPending || tipiDisponibili.length === 0}
            className="btn-primary flex-1"
          >
            {invioMut.isPending ? 'Invio...' : tipiDisponibili.length === 0 ? 'Nessun documento nuovo' : `Invia richiesta${tipiDisponibili.length > 1 ? ` (${tipiDisponibili.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}

function BeniAttrezzature({ tecnicoId, tecnicoNome }: {
  tecnicoId: number; tecnicoNome: string
}) {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editBene, setEditBene] = useState<any | null>(null)
  const [showVerbali, setShowVerbali] = useState(false)
  const [invioLoading, setInvioLoading] = useState<number | null>(null)
  const [fotoUploading, setFotoUploading] = useState<number | null>(null)
  const fotoInputRef = useRef<HTMLInputElement>(null)
  const [fotoTargetId, setFotoTargetId] = useState<number | null>(null)
  const formCamRef = useRef<HTMLInputElement>(null)
  const [fotoForm, setFotoForm] = useState<File | null>(null)

  const emptyForm = {
    descrizione: '', seriale: '', quantita: 1,
    unita: 'pz', data_consegna: new Date().toISOString().slice(0, 10),
    note: '', categoria: 'altro', campi_extra: {} as Record<string, string>,
  }
  const [form, setForm] = useState(emptyForm)

  const { data: beni = [], isLoading } = useQuery({
    queryKey: ['beni', tecnicoId],
    queryFn: () => beniApi.lista(tecnicoId).then(r => r.data),
    enabled: !!tecnicoId,
  })

  const { data: assegnazioni = [] } = useQuery({
    queryKey: ['assegnazioni', tecnicoId],
    queryFn: () => verbaliApi.listaAssegnazioni(tecnicoId).then(r => r.data),
    enabled: !!tecnicoId && showVerbali,
  })

  const f = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }))
  const fExtra = (k: string, v: string) => setForm(p => ({ ...p, campi_extra: { ...p.campi_extra, [k]: v } }))

  const applyPreset = (preset: typeof PRESET_BENI[0]) => {
    setForm({
      ...emptyForm,
      descrizione: preset.descrizione,
      categoria: preset.categoria,
      campi_extra: { ...preset.campi_extra } as Record<string, string>,
    })
  }

  const saveMut = useMutation({
    mutationFn: (): Promise<AxiosResponse<any>> => {
      const payload = {
        ...form,
        quantita: Number(form.quantita),
        campi_extra: form.categoria === 'automezzo' ? form.campi_extra : {},
      }
      return editBene
        ? beniApi.aggiorna(tecnicoId, editBene.id, payload)
        : beniApi.crea(tecnicoId, payload)
    },
    onSuccess: async (res) => {
      const isNew = !editBene
      if (isNew && fotoForm) {
        const nuovoBeneId = res.data?.id
        if (nuovoBeneId) {
          try {
            await beniApi.uploadFoto(tecnicoId, nuovoBeneId, fotoForm)
          } catch { /* foto non critica */ }
        }
        setFotoForm(null)
      }
      qc.invalidateQueries({ queryKey: ['beni', tecnicoId] })
      setShowForm(false)
      setEditBene(null)
      setForm(emptyForm)
      toast.success(editBene ? 'Bene aggiornato' : 'Bene aggiunto')
    },
    onError: () => toast.error('Errore salvataggio'),
  })

  const delMut = useMutation({
    mutationFn: (id: number) => beniApi.elimina(tecnicoId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['beni', tecnicoId] })
      toast.success('Bene eliminato')
    },
  })

  const creaAssegnazioneMut = useMutation({
    mutationFn: () => verbaliApi.creaAssegnazione({
      tecnico_id: tecnicoId,
      data_assegnazione: new Date().toISOString().slice(0, 10),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assegnazioni', tecnicoId] })
      setShowVerbali(true)
      toast.success('Verbale creato — ora puoi inviarlo')
    },
    onError: () => toast.error('Errore creazione verbale'),
  })

  const handleInviaVerbale = async (assegnazioneId: number) => {
    setInvioLoading(assegnazioneId)
    try {
      const res = await verbaliApi.inviaVerbale(assegnazioneId)
      qc.invalidateQueries({ queryKey: ['assegnazioni', tecnicoId] })
      toast.success(`Verbale inviato a ${res.data.email_destinatario}`)
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Errore invio verbale'
      toast.error(typeof msg === 'string' ? msg : 'Errore invio verbale')
    } finally {
      setInvioLoading(null)
    }
  }

  const handleScaricaFirmato = async (assegnazioneId: number) => {
    try {
      const res = await verbaliApi.scaricaFirmato(assegnazioneId)
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a'); a.href = url
      a.download = `verbale_firmato_${assegnazioneId}.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Errore download verbale firmato') }
  }

  const handleScaricaBozza = async (assegnazioneId: number) => {
    try {
      const res = await verbaliApi.scaricaBozza(assegnazioneId)
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a'); a.href = url
      a.download = `verbale_bozza_${assegnazioneId}.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Errore download bozza') }
  }

  const handleEliminaAssegnazione = (assegnazioneId: number) => {
    confirmDelete('Eliminare questa assegnazione?', async () => {
      try {
        await verbaliApi.eliminaAssegnazione(assegnazioneId)
        qc.invalidateQueries({ queryKey: ['assegnazioni', tecnicoId] })
        toast.success('Assegnazione eliminata')
      } catch (err: any) {
        const msg = err.response?.data?.detail || 'Errore eliminazione'
        toast.error(typeof msg === 'string' ? msg : 'Errore eliminazione')
      }
    })
  }

  const handleRiapri = async (assegnazioneId: number) => {
    if (!window.confirm('Riaprire il verbale firmato? Verrà riportato in bozza per modifiche.')) return
    try {
      await verbaliApi.riapriFirmato(assegnazioneId)
      qc.invalidateQueries({ queryKey: ['assegnazioni', tecnicoId] })
      toast.success('Verbale riaperto — ora puoi modificarlo e re-inviarlo')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Errore riapertura')
    }
  }

  const handleCreaRiconsegna = async (assegnazioneId: number) => {
    if (!window.confirm('Creare un documento di riconsegna? Verrà generato un nuovo verbale per la restituzione dei beni.')) return
    try {
      await verbaliApi.creaRiconsegna(assegnazioneId)
      qc.invalidateQueries({ queryKey: ['assegnazioni', tecnicoId] })
      toast.success('Documento di riconsegna creato')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Errore creazione riconsegna')
    }
  }

  const copiaTokea = (token: string) => {
    const url = `${window.location.origin}/firma-verbale/${token}`
    navigator.clipboard.writeText(url).then(() => toast.success('Link copiato'))
  }

  const handlePdf = async () => {
    try {
      const res = await beniApi.pdf(tecnicoId)
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a'); a.href = url
      a.download = `verbale_consegna_${tecnicoNome.replace(/\s+/g, '_')}.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Errore generazione PDF') }
  }

  const openEdit = (b: any) => {
    setEditBene(b)
    setForm({
      descrizione: b.descrizione, seriale: b.seriale || '',
      quantita: b.quantita, unita: b.unita,
      data_consegna: b.data_consegna, note: b.note || '',
      categoria: b.categoria || 'altro',
      campi_extra: b.campi_extra || {},
    })
    setShowForm(true)
  }

  const handleFotoClick = (beneId: number) => {
    setFotoTargetId(beneId)
    fotoInputRef.current?.click()
  }

  const handleFotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !fotoTargetId) return
    setFotoUploading(fotoTargetId)
    try {
      await beniApi.uploadFoto(tecnicoId, fotoTargetId, file)
      qc.invalidateQueries({ queryKey: ['beni', tecnicoId] })
      toast.success('Foto allegata')
    } catch { toast.error('Errore upload foto') }
    finally {
      setFotoUploading(null)
      setFotoTargetId(null)
      if (fotoInputRef.current) fotoInputRef.current.value = ''
    }
  }

  const handleEliminaFoto = (beneId: number, idx: number) => {
    confirmDelete('Eliminare questa foto?', async () => {
      try {
        await beniApi.eliminaFoto(tecnicoId, beneId, idx)
        qc.invalidateQueries({ queryKey: ['beni', tecnicoId] })
        toast.success('Foto eliminata')
      } catch { toast.error('Errore eliminazione foto') }
    })
  }

  const UNITA = ['pz', 'kit', 'set', 'mt', 'lt', 'kg', 'coppia', 'paio']

  return (
    <div className="space-y-4">
      {ConfirmModal}
      {/* input foto nascosto */}
      <input
        ref={fotoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFotoChange}
      />

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          🔧 Beni, Attrezzature e DPI
          <span className="text-xs text-slate-400 font-normal">
            ({(beni as any[]).length} voci)
          </span>
        </h3>
        <div className="flex gap-2">
          {(beni as any[]).length > 0 && (
            <button onClick={handlePdf} className="btn-secondary text-sm" style={{ borderColor: '#1E40AF', color: '#1E40AF' }}>
              📄 Stampa PDF
            </button>
          )}
          <button onClick={() => { setEditBene(null); setForm(emptyForm); setFotoForm(null); setShowForm(true) }} className="btn-primary text-sm">
            + Aggiungi
          </button>
        </div>
      </div>

      {/* ── Form aggiunta/modifica ── */}
      {showForm && (
        <div className="card p-4 border-2 border-blue-200 bg-blue-50/30 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm text-blue-800">
              {editBene ? 'Modifica voce' : 'Nuova voce'}
            </h4>
            {!editBene && (
              <div className="flex gap-2">
                <span className="text-xs text-slate-500 self-center">Preset:</span>
                {PRESET_BENI.map(p => (
                  <button
                    key={p.categoria}
                    onClick={() => applyPreset(p)}
                    className="text-xs px-2 py-1 rounded border border-blue-300 bg-white text-blue-700 hover:bg-blue-50"
                  >
                    {p.categoria === 'automezzo' ? '🚗 Automezzo' : '🔧 Scaletta'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Riga 1: Categoria + Descrizione */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Categoria</label>
              <select className="select" value={form.categoria} onChange={e => f('categoria', e.target.value)}>
                {CATEGORIE.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Descrizione *</label>
              <input
                className="input"
                placeholder="es. Trapano Bosch, Casco di sicurezza..."
                value={form.descrizione}
                onChange={e => f('descrizione', e.target.value)}
              />
            </div>
          </div>

          {/* Riga 2: Seriale + Data */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">N° Seriale / Matricola</label>
              <input
                className="input"
                placeholder="es. SN12345 (opzionale)"
                value={form.seriale}
                onChange={e => f('seriale', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Data consegna *</label>
              <input type="date" className="input" value={form.data_consegna} onChange={e => f('data_consegna', e.target.value)} />
            </div>
          </div>

          {/* Riga 3: Quantità + Unità + Note */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Quantità *</label>
              <input
                type="number" min={1} step={0.5} className="input"
                value={form.quantita} onChange={e => f('quantita', parseFloat(e.target.value) || 1)}
              />
            </div>
            <div>
              <label className="label">Unità</label>
              <select className="select" value={form.unita} onChange={e => f('unita', e.target.value)}>
                {UNITA.map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Note</label>
              <input className="input" placeholder="Condizioni, note..." value={form.note} onChange={e => f('note', e.target.value)} />
            </div>
          </div>

          {/* Campi strutturati automezzo */}
          {form.categoria === 'automezzo' && (
            <div className="border border-blue-200 rounded-lg p-3 bg-white">
              <p className="text-xs font-semibold text-blue-700 mb-2">🚗 Dettagli Automezzo Aziendale</p>
              <div className="grid grid-cols-2 gap-3">
                {CAMPI_AUTOMEZZO.map(campo => (
                  <div key={campo.key}>
                    <label className="label">{campo.label}</label>
                    <input
                      className="input"
                      placeholder={campo.label}
                      value={form.campi_extra[campo.key] || ''}
                      onChange={e => fExtra(campo.key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Foto bene (solo nuova creazione) */}
          {!editBene && (
            <div>
              <input
                ref={formCamRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) setFotoForm(f); e.target.value = '' }}
              />
              <label className="label">Foto bene (opzionale)</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => formCamRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-300 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors"
                >
                  📷 Scatta foto
                </button>
                {fotoForm && (
                  <div className="flex items-center gap-2">
                    <img
                      src={URL.createObjectURL(fotoForm)}
                      alt="preview"
                      className="w-12 h-12 object-cover rounded border border-slate-200"
                    />
                    <span className="text-xs text-slate-500 max-w-[140px] truncate">{fotoForm.name}</span>
                    <button
                      type="button"
                      onClick={() => setFotoForm(null)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >✕</button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={() => { setShowForm(false); setEditBene(null); setFotoForm(null) }} className="btn-secondary flex-1">Annulla</button>
            <button
              onClick={() => saveMut.mutate()}
              disabled={!form.descrizione || saveMut.isPending}
              className="btn-primary flex-1"
            >
              {saveMut.isPending ? 'Salvataggio...' : editBene ? 'Aggiorna' : 'Aggiungi'}
            </button>
          </div>
        </div>
      )}

      {/* ── Lista beni ── */}
      {isLoading ? (
        <div className="text-center text-slate-400 py-8">Caricamento...</div>
      ) : (beni as any[]).length === 0 ? (
        <div className="text-center text-slate-400 py-8 text-sm">
          Nessun bene assegnato.<br/>Clicca "+ Aggiungi" o scegli un preset.
        </div>
      ) : (
        <div className="space-y-2">
          {(beni as any[]).map((b: any, i: number) => {
            const catInfo = CATEGORIE.find(c => c.value === (b.categoria || 'altro'))
            const extra = b.campi_extra || {}
            const extraEntries = b.categoria === 'automezzo'
              ? CAMPI_AUTOMEZZO.filter(c => extra[c.key]?.trim()).map(c => ({ label: c.label, val: extra[c.key] }))
              : []
            return (
              <div key={b.id} className="card p-3 border border-slate-200 hover:border-blue-200 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-slate-400 font-mono">{i + 1}.</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{catInfo?.label}</span>
                      <span className="font-medium text-slate-800 text-sm">{b.descrizione}</span>
                      {b.seriale && <span className="font-mono text-xs text-slate-400">SN: {b.seriale}</span>}
                      <span className="text-xs text-slate-400">
                        {Number(b.quantita) % 1 === 0 ? parseInt(b.quantita) : b.quantita} {b.unita}
                      </span>
                      <span className="text-xs text-slate-400">
                        {b.data_consegna ? new Date(b.data_consegna).toLocaleDateString('it-IT') : '—'}
                      </span>
                    </div>

                    {/* Dettagli automezzo */}
                    {extraEntries.length > 0 && (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1.5 text-xs text-slate-500">
                        {extraEntries.map(e => (
                          <span key={e.label}><span className="font-medium text-slate-600">{e.label}:</span> {e.val}</span>
                        ))}
                      </div>
                    )}

                    {b.note && <p className="text-xs text-slate-400 mt-1 italic">{b.note}</p>}

                    {/* Foto allegate */}
                    {(b.foto_paths || []).length > 0 && (
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {(b.foto_paths as string[]).map((fp, idx) => {
                          const fname = fp.split('/').pop()
                          const beneId = b.id
                          const urlParts = fp.split('beni_foto/')
                          const imgUrl = urlParts.length > 1 ? `/api/uploads/beni_foto/${urlParts[1]}` : ''
                          return (
                            <div key={idx} className="relative group">
                              {imgUrl ? (
                                <img
                                  src={imgUrl}
                                  alt={`foto ${idx+1}`}
                                  className="w-14 h-14 object-cover rounded border border-slate-200 cursor-pointer"
                                  onClick={() => window.open(imgUrl, '_blank')}
                                />
                              ) : (
                                <div className="w-14 h-14 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-xs text-slate-400">
                                  📷
                                </div>
                              )}
                              <button
                                onClick={() => handleEliminaFoto(beneId, idx)}
                                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs hidden group-hover:flex items-center justify-center leading-none"
                                title="Elimina foto"
                              >×</button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => handleFotoClick(b.id)}
                      disabled={fotoUploading === b.id}
                      className="btn-ghost px-2 py-1 text-xs text-slate-500"
                      title="Allega foto"
                    >
                      {fotoUploading === b.id ? '...' : '📷'}
                    </button>
                    <button onClick={() => openEdit(b)} className="btn-ghost px-2 py-1 text-xs text-blue-600" title="Modifica">✏</button>
                    <button
                      onClick={() => confirmDelete(`Eliminare "${b.descrizione}"?`, () => delMut.mutate(b.id))}
                      className="btn-ghost px-2 py-1 text-xs text-red-500"
                      title="Elimina"
                    >🗑</button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ── Verbali con Firma Digitale ── */}
      {(beni as any[]).length > 0 && (
        <div className="card p-4 border border-indigo-100 bg-indigo-50/30">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-sm text-indigo-900">✍ Verbali con Firma Digitale</h4>
            <div className="flex gap-2">
              <button onClick={() => setShowVerbali(v => !v)} className="btn-ghost text-xs text-indigo-700 px-2 py-1">
                {showVerbali ? 'Nascondi' : 'Mostra storico'}
              </button>
              <button
                onClick={() => creaAssegnazioneMut.mutate()}
                disabled={creaAssegnazioneMut.isPending}
                className="btn-primary text-xs"
                style={{ background: '#4338ca', padding: '4px 12px' }}
              >
                {creaAssegnazioneMut.isPending ? '...' : '+ Nuovo verbale'}
              </button>
            </div>
          </div>
          <p className="text-xs text-indigo-700 mb-3">
            Genera un verbale digitale e invialo via email per la firma. Puoi riaprire un verbale firmato o creare un documento di riconsegna.
          </p>

          {showVerbali && (
            <div className="space-y-2">
              {(assegnazioni as any[]).length === 0 ? (
                <div className="text-center text-slate-400 py-4 text-xs">
                  Nessun verbale. Clicca "+ Nuovo verbale" per iniziare.
                </div>
              ) : (
                (assegnazioni as any[]).map((a: any) => {
                  const isRiconsegna = a.tipo_documento === 'riconsegna'
                  return (
                    <div key={a.id} className={`bg-white rounded-lg border p-3 ${isRiconsegna ? 'border-red-200' : 'border-slate-200'}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-slate-400 font-mono">#{a.id}</span>
                          {isRiconsegna && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">RICONSEGNA</span>
                          )}
                          <StatoVerbale stato={a.stato} />
                          <span className="text-xs text-slate-500">
                            {new Date(a.data_assegnazione).toLocaleDateString('it-IT')}
                          </span>
                          {a.data_invio && (
                            <span className="text-xs text-slate-400">
                              Inviato: {new Date(a.data_invio).toLocaleDateString('it-IT')}
                            </span>
                          )}
                          {a.data_firma && (
                            <span className="text-xs text-green-600 font-medium">
                              Firmato: {new Date(a.data_firma).toLocaleString('it-IT')}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          {a.stato === 'bozza' && (
                            <button
                              onClick={() => handleInviaVerbale(a.id)}
                              disabled={invioLoading === a.id}
                              className="btn-primary text-xs"
                              style={{ padding: '3px 10px', fontSize: 11, background: isRiconsegna ? '#991b1b' : undefined }}
                            >
                              {invioLoading === a.id ? '...' : '📧 Invia'}
                            </button>
                          )}
                          {a.stato === 'inviata' && (
                            <>
                              <button
                                onClick={() => handleInviaVerbale(a.id)}
                                disabled={invioLoading === a.id}
                                className="btn-secondary text-xs"
                                style={{ padding: '3px 10px', fontSize: 11, borderColor: '#f97316', color: '#f97316' }}
                              >
                                {invioLoading === a.id ? '...' : '↻ Re-invia'}
                              </button>
                              {a.token_firma && (
                                <button
                                  onClick={() => copiaTokea(a.token_firma)}
                                  className="btn-secondary text-xs"
                                  style={{ padding: '3px 10px', fontSize: 11 }}
                                >
                                  🔗 Copia link
                                </button>
                              )}
                            </>
                          )}
                          {a.stato === 'firmata' && (
                            <>
                              <button
                                onClick={() => handleScaricaFirmato(a.id)}
                                className="btn-secondary text-xs"
                                style={{ padding: '3px 10px', fontSize: 11, borderColor: '#16a34a', color: '#16a34a' }}
                              >
                                📄 Scarica PDF
                              </button>
                              {!isRiconsegna && (
                                <button
                                  onClick={() => handleCreaRiconsegna(a.id)}
                                  className="btn-secondary text-xs"
                                  style={{ padding: '3px 10px', fontSize: 11, borderColor: '#991b1b', color: '#991b1b' }}
                                  title="Crea documento di riconsegna"
                                >
                                  ↩ Riconsegna
                                </button>
                              )}
                              <button
                                onClick={() => handleRiapri(a.id)}
                                className="btn-ghost text-xs"
                                style={{ padding: '3px 10px', fontSize: 11, color: '#6366f1' }}
                                title="Riapri per modifiche"
                              >
                                🔓 Riapri
                              </button>
                            </>
                          )}
                          {(a.stato === 'inviata' || a.stato === 'bozza') && (
                            <button
                              onClick={() => handleScaricaBozza(a.id)}
                              className="btn-ghost text-xs"
                              style={{ padding: '3px 10px', fontSize: 11, color: '#6b7280' }}
                            >
                              🗂 Bozza
                            </button>
                          )}
                          {a.stato !== 'firmata' && (
                            <button
                              onClick={() => handleEliminaAssegnazione(a.id)}
                              className="btn-ghost text-xs"
                              style={{ padding: '3px 10px', fontSize: 11, color: '#ef4444' }}
                            >
                              🗑
                            </button>
                          )}
                        </div>
                      </div>
                      {a.beni_snapshot && a.beni_snapshot.length > 0 && (
                        <div className="mt-2 text-xs text-slate-500">
                          {a.beni_snapshot.length} beni nel verbale
                          {a.beni_snapshot.slice(0, 2).map((bx: any, ix: number) => (
                            <span key={ix} className="ml-2 text-slate-400">· {bx.descrizione}</span>
                          ))}
                          {a.beni_snapshot.length > 2 && <span className="text-slate-400"> +{a.beni_snapshot.length - 2} altri</span>}
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
